import { IconEdit, IconPlus } from '@tabler/icons-react';
import { convertToYesNo, isDefined } from '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Button } from '@zorro/design-system/v2/Button';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { Loader } from '@zorro/design-system/v2/Loader';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  formatCurrencyEnUs,
  formatDateEnLocale,
  formatDateEnLocaleRange,
  parseDateISO,
} from '@zorro/shared/formatters';
import { oepPaymentMethodLabelConfig } from '@zorro/types';
import { Box, DataTable, Group } from '@zorro/web/design-system';
import {
  useEmployerFeatureFlag,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import {
  DataTableColumn,
  DataTableColumnGroup,
  DataTableSortStatus,
} from 'mantine-datatable';
import { useState } from 'react';

import { PlanYearSetupForm } from '../PlanYearSetupForm/PlanYearSetupForm';
import { AllowanceModelCell } from './cells/AllowanceModelCell';
import { NO_DATA_PLACEHOLDER } from './planYearTable.consts';

type PlanYearRecord = {
  id: string;
  year: string;
  effectivePeriod: string;
  openEnrollmentPeriod: string;
  isDomesticPartnerAllowed: string;
  paymentMethod: string;
  initialDrawAmount: string;
  initialDrawDate: string;
  reserveAmount: string;
  thresholdAmount: string;
  isAORChanged: string;
};

type Props = {
  employerName: string;
  employerId: string;
  isEditable: boolean;
};

export const PlanYearsTable = ({
  employerName,
  employerId,
  isEditable,
}: Props) => {
  const { isEmployerAdmin } = useRoles();
  const [isPlanYearDrawerOpen, setIsPlanYearDrawerOpen] = useState(false);
  const [editPeriod, setEditPeriod] = useState<PlanYearRecord>();
  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<PlanYearRecord>
  >({
    columnAccessor: 'year',
    direction: 'desc',
  });

  const { isOn: isLynxPaymentsEnabled, isReady: isLynxFlagReady } =
    useEmployerFeatureFlag('lynx_payments_enabled', employerId);

  const { data: openEnrollmentPeriods = [], isLoading: isLoadingOEPs } =
    useMonolithQuery({
      method: 'openEnrollmentPeriodsControllerFindAllForEmployer',
      params: [employerId],
    });

  const handleClose = () => {
    setIsPlanYearDrawerOpen(false);
  };

  if (isLoadingOEPs || !isLynxFlagReady) {
    return <Loader />;
  }

  const planYearRecords: PlanYearRecord[] = openEnrollmentPeriods.map(
    ({
      id,
      effectiveFrom,
      effectiveUntil,
      onboardingFrom,
      onboardingUntil,
      paymentMethod,
      initialDrawAmount,
      initialDrawDate,
      reserveAmount,
      thresholdAmount,
      isAORChanged,
      isDomesticPartnerAllowed,
    }) => {
      return {
        id,
        year: parseDateISO(effectiveFrom).year().toString(),
        effectivePeriod: formatDateEnLocaleRange(effectiveFrom, effectiveUntil),
        openEnrollmentPeriod: formatDateEnLocaleRange(
          onboardingFrom,
          onboardingUntil
        ),
        isDomesticPartnerAllowed: convertToYesNo(isDomesticPartnerAllowed),
        paymentMethod: paymentMethod
          ? oepPaymentMethodLabelConfig[paymentMethod]
          : NO_DATA_PLACEHOLDER,
        initialDrawAmount: isDefined(initialDrawAmount)
          ? formatCurrencyEnUs(initialDrawAmount)
          : NO_DATA_PLACEHOLDER,
        initialDrawDate: isDefined(initialDrawDate)
          ? formatDateEnLocale(initialDrawDate)
          : NO_DATA_PLACEHOLDER,
        reserveAmount: isDefined(reserveAmount)
          ? formatCurrencyEnUs(reserveAmount)
          : NO_DATA_PLACEHOLDER,
        thresholdAmount: isDefined(thresholdAmount)
          ? formatCurrencyEnUs(thresholdAmount)
          : NO_DATA_PLACEHOLDER,
        isAORChanged: isDefined(isAORChanged)
          ? convertToYesNo(isAORChanged)
          : NO_DATA_PLACEHOLDER,
      };
    }
  );

  const dataToSort = planYearRecords.sort((first, second) => {
    return (first[sortStatus.columnAccessor as keyof PlanYearRecord] ?? '')
      .toLocaleString()
      .localeCompare(
        (
          second[sortStatus.columnAccessor as keyof PlanYearRecord] ?? ''
        ).toLocaleString()
      );
  });

  const records =
    sortStatus.direction === 'desc' ? dataToSort.reverse() : dataToSort;

  const planDesignColumns: DataTableColumn<PlanYearRecord>[] = [
    {
      accessor: 'year',
      title: 'Year',
      sortable: true,
      width: 120,
    },
    {
      accessor: 'effectivePeriod',
      title: 'Coverage period',
      width: 250,
    },
    {
      accessor: 'openEnrollmentPeriod',
      title: 'Election window',
      width: 250,
    },
    {
      accessor: 'allowanceModelFile',
      title: 'Allowance model file',
      render: ({ year }) => (
        <AllowanceModelCell
          employerId={employerId}
          employerName={employerName}
          year={year}
        />
      ),
      width: isEmployerAdmin ? '170px' : 'auto',
    },
    {
      accessor: 'isDomesticPartnerAllowed',
      title: 'Domestic partners allowed',
    },
  ];

  const fullPlanYearGroups: DataTableColumnGroup<PlanYearRecord>[] = [
    {
      id: 'plan_design',
      title: (
        <Box ta="center" w="100%">
          Plan design
        </Box>
      ),
      columns: planDesignColumns,
    },
    {
      id: 'payments',
      title: (
        <Box ta="center" w="100%">
          Payments
        </Box>
      ),
      columns: [
        {
          accessor: 'paymentMethod',
          title: 'Payment method',
          width: 230,
        },
        {
          accessor: 'initialDrawAmount',
          title: 'Initial draw amount',
        },
        {
          accessor: 'initialDrawDate',
          title: 'Initial draw date',
        },
        {
          accessor: 'reserveAmount',
          title: 'Reserve amount',
        },
        ...(isLynxPaymentsEnabled
          ? [
              {
                accessor: 'thresholdAmount' as const,
                title: 'Threshold amount',
              },
            ]
          : []),
      ],
    },
    {
      id: 'agentOfRecord',
      title: (
        <Box ta="center" w="100%">
          Agent of record
        </Box>
      ),
      columns: [
        {
          accessor: 'isAORChanged',
          title: 'AOR switch',
          width: 200,
        },
      ],
    },
    ...(isEditable
      ? [
          {
            id: 'ActionsItems',
            title: '',
            columns: [
              {
                title: 'Actions',
                accessor: 'actions',
                sortable: false,
                render: (record: PlanYearRecord) => (
                  <ActionIcon
                    size="md"
                    onClick={() => {
                      setEditPeriod(record);
                      setIsPlanYearDrawerOpen(true);
                    }}
                  >
                    <IconEdit />
                  </ActionIcon>
                ),
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <>
      <Drawer
        title={editPeriod ? 'Edit plan year' : 'New plan year'}
        onClose={handleClose}
        opened={isPlanYearDrawerOpen}
      >
        <PlanYearSetupForm
          openEnrollmentPeriodId={editPeriod?.id}
          isEdit={Boolean(editPeriod)}
          employerId={employerId}
          onSuccess={handleClose}
        />
      </Drawer>

      {isEditable && (
        <Group
          justify="flex-end"
          pr="var(--space-24)"
          mt="calc(-1 * (var(--mantine-spacing-xl) + var(--mantine-spacing-xs)))"
          mb="calc(-1 * var(--mantine-spacing-xs))"
        >
          <Tooltip label="Add a new plan year">
            <Button
              size="xs"
              variant="primary"
              leftSection={<IconPlus />}
              aria-label="Add plan year"
              onClick={() => {
                setEditPeriod(undefined);
                setIsPlanYearDrawerOpen(true);
              }}
            />
          </Tooltip>
        </Group>
      )}

      <DataTable
        shouldGrow
        records={records}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        {...(isEmployerAdmin
          ? { columns: planDesignColumns }
          : { groups: fullPlanYearGroups })}
      />
    </>
  );
};
